import { CheckboxInput } from "@astryxdesign/core/CheckboxInput";
import { CheckboxList, CheckboxListItem } from "@astryxdesign/core/CheckboxList";
import { Collapsible } from "@astryxdesign/core/Collapsible";
import { Card } from "@astryxdesign/core/Card";
import { Badge } from "@astryxdesign/core/Badge";
import { FieldStatus } from "@astryxdesign/core/FieldStatus";
import { HStack } from "@astryxdesign/core/HStack";
import { VStack } from "@astryxdesign/core/VStack";
import { Text } from "@astryxdesign/core/Text";
import type { Camera, CameraScope, Project } from "../../lib/api";

interface CameraAccessEditorProps {
  /** Whether access is narrowed at all. Kept separate from `scope` itself
   *  because CameraScope's zero value (both arrays empty) is the same
   *  shape whether the caller means "unrestricted" or "restricted, nothing
   *  picked yet" — the parent dialog owns this bit so the tree doesn't
   *  collapse back to "Unrestricted" the moment every box is unchecked. */
  isRestricted: boolean;
  onRestrictedChange: (restricted: boolean) => void;
  scope: CameraScope;
  onScopeChange: (scope: CameraScope) => void;
  projects: Project[];
  cameras: Camera[];
  isDisabled?: boolean;
  /** Validation feedback for the "restricted but nothing selected" case —
   *  this editor has no single input to attach a TextInput-style `status`
   *  to, so the caller passes one here instead of falling back to a
   *  page-level error string. */
  status?: { type: "error" | "warning" | "success"; message: string };
}

// DMS-style hierarchical access editor: one row per project with a
// tri-state "grant the whole project" checkbox, expandable to individual
// per-camera checkboxes. Checking a project writes to CameraScope.ProjectIDs
// (present AND future cameras); checking individual cameras writes to
// CameraIDs instead — see CameraScope's doc comment on the backend for why
// those are two different grants, not one.
export function CameraAccessEditor({
  isRestricted,
  onRestrictedChange,
  scope,
  onScopeChange,
  projects,
  cameras,
  isDisabled,
  status,
}: CameraAccessEditorProps) {
  return (
    <VStack gap={3}>
      <CheckboxInput
        label="Restrict camera access"
        description="Off grants access to every camera in every project, including ones added later."
        value={isRestricted}
        onChange={onRestrictedChange}
        isDisabled={isDisabled}
      />

      {isRestricted &&
        (projects.length === 0 ? (
          <Text type="supporting" color="secondary">
            No projects yet — add one on the Projects tab first.
          </Text>
        ) : (
          <VStack gap={2}>
            {projects.map((project) => {
              const projectCameras = cameras.filter((c) => c.project_id === project.id);
              const projectCameraIds = projectCameras.map((c) => c.id);
              const projectGranted = scope.project_ids.includes(project.id);
              const grantedCameraIdsHere = projectCameraIds.filter((id) => scope.camera_ids.includes(id));

              const projectCheckboxValue: boolean | "indeterminate" = projectGranted
                ? true
                : grantedCameraIdsHere.length > 0
                  ? "indeterminate"
                  : false;

              const toggleProject = (checked: boolean) => {
                const camerasOutsideThisProject = scope.camera_ids.filter((id) => !projectCameraIds.includes(id));
                onScopeChange({
                  project_ids: checked
                    ? [...scope.project_ids, project.id]
                    : scope.project_ids.filter((id) => id !== project.id),
                  // Either way, per-camera grants inside this project become
                  // redundant (granting) or revoked (ungranting) — keeping
                  // them around would let a later "select all" uncheck leave
                  // stale individual grants behind.
                  camera_ids: camerasOutsideThisProject,
                });
              };

              const changeCamerasHere = (newIdsHere: string[]) => {
                const camerasOutsideThisProject = scope.camera_ids.filter((id) => !projectCameraIds.includes(id));
                onScopeChange({ project_ids: scope.project_ids, camera_ids: [...camerasOutsideThisProject, ...newIdsHere] });
              };

              return (
                <Card key={project.id} padding={0} elevation="low">
                  <HStack gap={2} vAlign="center" padding={2}>
                    <CheckboxInput
                      label={`Grant all cameras in ${project.name}`}
                      isLabelHidden
                      value={projectCheckboxValue}
                      onChange={toggleProject}
                      isDisabled={isDisabled}
                    />
                    <VStack gap={0} style={{ flex: 1 }}>
                      <Collapsible
                        defaultIsOpen={false}
                        trigger={
                          <HStack gap={1.5} vAlign="center">
                            <Text weight="medium">{project.name}</Text>
                            <Badge
                              variant="neutral"
                              label={`${projectCameras.length} camera${projectCameras.length === 1 ? "" : "s"}`}
                            />
                            {projectGranted && <Badge variant="blue" label="All granted" />}
                          </HStack>
                        }
                      >
                        <VStack padding={2} paddingBlockStart={0}>
                          {projectCameras.length === 0 ? (
                            <Text type="supporting" color="secondary">
                              No cameras in this project yet.
                            </Text>
                          ) : (
                            <CheckboxList
                              label={`Cameras in ${project.name}`}
                              isLabelHidden
                              value={projectGranted ? projectCameraIds : grantedCameraIdsHere}
                              onChange={changeCamerasHere}
                              isDisabled={isDisabled || projectGranted}
                              disabledMessage={projectGranted ? "Already granted via the whole project" : undefined}
                              hasDividers
                            >
                              {projectCameras.map((camera) => (
                                <CheckboxListItem key={camera.id} value={camera.id} label={camera.name} />
                              ))}
                            </CheckboxList>
                          )}
                        </VStack>
                      </Collapsible>
                    </VStack>
                  </HStack>
                </Card>
              );
            })}
          </VStack>
        ))}

      {status && <FieldStatus type={status.type} message={status.message} variant="detached" />}
    </VStack>
  );
}
